import { SegmentedControl } from '@lawpilots/design-system'
import { Box, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { ListPagination, TableNoDataRow } from '@/common/components'
import { LoadingPanel } from '@/common/components/LoadingPanel/LoadingPanel'
import { useSearchTenantHierarchies } from '@/features/tenant/hooks/useSearchTenantHierarchies'
import { useTenantSearchFormContext } from '@/features/tenant/hooks/useTenantSearchFormContext'
import { usePagedListState } from '@/hooks'
import { TenantHierarchyFilter } from '@/services/api/tenant/subsidiaries'

import { TenantHierarchyTableRow } from './TenantHierarchyTableRow'
import {
  COMPANY_COLUMN_WIDTH,
  filterBarStyles,
  headerCellStyles,
  paginationStyles,
  setupHeaderCellStyles,
  tableContainerStyles,
  tableHeadStyles,
  tableStyles,
  tableWrapperStyles,
} from './TenantHierarchyTableStyles'

const FILTER_VALUES: TenantHierarchyFilter[] = ['all', 'standalone', 'parent', 'child']

export const TenantHierarchyTable = (): JSX.Element => {
  const { t, i18n } = useTranslation('general')
  const { values, setFieldValue } = useTenantSearchFormContext()
  const searchQuery = (values?.name || '').trim() || undefined
  const filterType: TenantHierarchyFilter = values?.type || 'all'
  const { page, size, onPageChange, onSizeChange } = usePagedListState({
    initialFilters: {},
    outsideFilters: { filterType, searchQuery },
  })

  const { data: list = { items: [], total: 0 }, isFetching, isUninitialized } = useSearchTenantHierarchies(page, size)
  const isLoading = isFetching || isUninitialized

  const handleFilterChange = useCallback(
    (newFilter: string) => {
      const filter = FILTER_VALUES.find((value) => value === newFilter)
      if (filter) setFieldValue('type', filter)
    },
    [setFieldValue],
  )

  const filterOptions: { value: TenantHierarchyFilter; label: string }[] = [
    { value: 'all', label: t('tenant_filter_all') },
    { value: 'standalone', label: t('tenant_type_standalone') },
    { value: 'parent', label: t('tenant_type_parent') },
    { value: 'child', label: t('tenant_table_subsidiaries') },
  ]

  return (
    <>
      <Stack direction="row" sx={filterBarStyles}>
        <SegmentedControl
          aria-label={t('tenant_filter_by_setup_type')}
          options={filterOptions}
          value={filterType}
          onChange={handleFilterChange}
        />
      </Stack>
      <LoadingPanel loading={isLoading} label={t('customers_loading')} key={i18n.language}>
        <Box sx={tableWrapperStyles}>
          <TableContainer sx={tableContainerStyles}>
            <Table sx={tableStyles}>
              <colgroup>
                <col style={{ width: COMPANY_COLUMN_WIDTH }} />
                <col />
              </colgroup>
              <TableHead sx={tableHeadStyles}>
                <TableRow>
                  <TableCell sx={headerCellStyles}>{t('company')}</TableCell>
                  <TableCell sx={setupHeaderCellStyles}>{t('tenant_table_setup_type')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.items.length === 0 ? (
                  <TableNoDataRow colSpan={2} message={t('noTenantsFound')} />
                ) : (
                  list.items.map((item) => (
                    <TenantHierarchyTableRow key={item.tenant.id} item={item} searchQuery={searchQuery} />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={paginationStyles}>
          <ListPagination
            page={page}
            pageCount={Math.ceil(list.total / size)}
            onPageChange={onPageChange}
            pageSize={size}
            onPageSizeChange={onSizeChange}
          />
        </Box>
      </LoadingPanel>
    </>
  )
}
